import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { usePersonas } from '@/hooks/usePersonas';
import { useContexts } from '@/hooks/useContexts';
import { useConversations } from '@/hooks/useConversations';
import { useInsights } from '@/hooks/useInsights';
import { getProfileCompleteness } from '@/lib/profileCompleteness';
import { calculateLifecycleStage } from '@/lib/lifecycleCalculator';
import {
  syncHubspotUsageIfChanged,
  recordLoginAfterExpired,
  getLoginsAfterExpired,
} from '@/services/hubspotService';

/**
 * Hook global que sincroniza silenciosamente as métricas de uso (PQL) e ciclo de vida com o HubSpot CRM.
 * Dispara automaticamente sempre que o total de personas, contextos, conversas, insights ou ciclo de vida é alterado.
 * Conta com proteção de cache local em localStorage para evitar requisições redundantes de rede.
 */
export function useSyncUsageTelemetry(): void {
  const { user, profile } = useAuth();
  const { tier, isExpired, isLoading: loadingSubscription } = useSubscription();
  const { personas, loading: loadingPersonas } = usePersonas();
  const { contexts, loading: loadingContexts } = useContexts();
  const { conversations, loading: loadingConversations } = useConversations();
  const { insights, loading: loadingInsights } = useInsights();

  useEffect(() => {
    // Não sincroniza se o usuário não estiver logado ou se as queries ainda estiverem em carregamento inicial
    if (
      !user?.email ||
      loadingSubscription ||
      loadingPersonas ||
      loadingContexts ||
      loadingConversations ||
      loadingInsights
    ) {
      return;
    }

    const keyPrefix = user.id || user.email;

    // Se a conta estiver expirada, registra o acesso nessa sessão
    let loginsAfterExpired = getLoginsAfterExpired(keyPrefix);
    if (isExpired) {
      loginsAfterExpired = recordLoginAfterExpired(keyPrefix);
    }

    const completeness = getProfileCompleteness(profile);

    const { lifecycleStage, leadStatus } = calculateLifecycleStage({
      tier,
      isExpired,
      isProfileComplete: completeness.isComplete,
      total_personas: personas.length,
      total_conversas: conversations.length,
      loginsAfterExpired,
    });

    syncHubspotUsageIfChanged({
      email: user.email,
      userId: user.id,
      total_personas: personas.length,
      total_contextos: contexts.length,
      total_conversas: conversations.length,
      total_insights: insights.length,
      lifecyclestage: lifecycleStage,
      hs_lead_status: leadStatus,
      closedate: lifecycleStage === 'customer' ? String(Date.now()) : undefined,
    }).catch((err) => {
      console.warn('[HubSpot] Falha ao sincronizar telemetria de uso e ciclo de vida:', err);
    });
  }, [
    user?.email,
    user?.id,
    profile,
    tier,
    isExpired,
    personas.length,
    contexts.length,
    conversations.length,
    insights.length,
    loadingSubscription,
    loadingPersonas,
    loadingContexts,
    loadingConversations,
    loadingInsights,
  ]);
}
